/**
 * NewAppointmentLightbox (v2)
 * ──────────────────────────────────────────────────────────────────────
 * The new "New Appointment" entry point per echo_appt_lightbox_and_visit_type_settings_v2.md.
 * - Mode selector: Agentic ("Ask Echo") / Manual / Lens
 * - 3-step Manual flow (Patient → Appointment → Confirm)
 * - Agentic: natural-language input + ambiguity resolution → Confirm
 * - Lens: scan-to-book (mocked extraction) → Confirm
 *
 * Demo-only. All data is props/fixtures. No API calls.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import {
  Sparkles, Mic, MicOff, ScanLine, ListOrdered, Search, Plus, ArrowLeft, UserPlus,
  CheckCircle2, Calendar, MapPin, User, Clock, AlertTriangle, IdCard, CreditCard,
  Wand2, Loader2,
} from 'lucide-react';
import { useAppMode } from '../../../../contexts/AppModeContext';

const MODES = [
  { id: 'agentic', title: 'Ask Echo', desc: '"Book for John at noon"', Icon: Sparkles },
  { id: 'manual', title: 'Manual', desc: 'Step-by-step form', Icon: ListOrdered },
  { id: 'lens', title: 'Lens', desc: 'Scan a doc or referral', Icon: ScanLine },
];

/* ── Reusable pill ── */
const Pill = ({ active, onClick, children, testId, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    style={{ border: 'none', cursor: disabled ? 'not-allowed' : 'pointer' }}
    className={cn(
      'px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors',
      active ? 'bg-[#111] text-white' : 'bg-[#f5f5f5] text-slate-600 hover:bg-slate-200',
      disabled && 'opacity-40'
    )}
  >
    {children}
  </button>
);

/* ── Mode Selector ── */
const ModeSelector = ({ onPick, last }) => (
  <div className="p-8 space-y-5" data-testid="appt-mode-selector">
    <div className="text-center">
      <h3 className="text-[13px] font-medium text-slate-900">How would you like to book?</h3>
    </div>
    <div className="grid grid-cols-3 gap-3">
      {MODES.map((m) => {
        const isLast = last === m.id;
        return (
          <button
            key={m.id}
            onClick={() => onPick(m.id)}
            data-testid={`appt-mode-${m.id}`}
            className={cn(
              'rounded-xl px-4 py-5 text-left transition-all hover:shadow-sm',
              isLast
                ? 'border-2 border-[#111] bg-slate-100'
                : 'border border-slate-200 bg-white hover:border-slate-300'
            )}
            style={{ cursor: 'pointer' }}
          >
            <m.Icon className={cn('h-4 w-4 mb-1.5', isLast ? 'text-[#111]' : 'text-slate-500')} />
            <p className="text-[12px] font-semibold text-slate-900">{m.title}</p>
            <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{m.desc}</p>
          </button>
        );
      })}
    </div>
    <p className="text-[10px] text-slate-400 text-center">Echo remembers your preference</p>
  </div>
);

/* ── Agentic mode ── */
const AgenticMode = ({ onSwitchManual, onSubmit, onNewPatient, onLensScan }) => {
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  return (
    <div className="p-6 space-y-4" data-testid="appt-agentic-mode">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[#111]" />
        <h3 className="text-[14px] font-semibold text-slate-900">Book an appointment</h3>
      </div>
      <div className="relative rounded-[10px] border border-slate-200 bg-slate-50 p-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={isDemo ? '"Book an appointment for John Smith with Dr. Chen at 12:30 PM at the Main Office"' : '"Book an appointment for {patient} with {provider} at {time}"'}
          className="w-full bg-transparent outline-none text-[12px] text-slate-800 resize-none placeholder:text-slate-400"
          data-testid="appt-agentic-input"
        />
        <button
          onClick={() => setListening(!listening)}
          className={cn(
            'absolute bottom-2 right-2 h-9 w-9 rounded-full inline-flex items-center justify-center transition-colors',
            listening ? 'bg-[#111] text-white animate-pulse' : 'bg-white border border-slate-200 text-slate-500 hover:text-[#111]'
          )}
          style={{ cursor: 'pointer' }}
          aria-label="Toggle voice input"
          data-testid="appt-agentic-mic"
        >
          <Mic className="h-4 w-4" />
        </button>
      </div>
      <p className="text-[10px] text-slate-400">Tap mic to speak · or type above and press Enter</p>
      <Button
        className="w-full h-10 bg-[#111] hover:bg-[#000] text-white text-[12px] font-semibold gap-1.5"
        onClick={() => onSubmit(text || (isDemo ? "Book an appointment for John Smith with Dr. Chen at 12:30 PM" : ""))}
        data-testid="appt-agentic-book"
      >
        <Sparkles className="h-3.5 w-3.5" /> Book it
      </Button>

      {/* Quick-action rail: New Patient + Lens ID / Insurance scan */}
      <div className="pt-1">
        <p className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide mb-1.5">Quick actions</p>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={onNewPatient}
            className="rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 p-2.5 text-left transition-colors"
            style={{ cursor: 'pointer' }}
            data-testid="appt-agentic-new-patient"
          >
            <UserPlus className="h-3.5 w-3.5 text-[#6b5ce7] mb-1" />
            <p className="text-[11px] font-semibold text-slate-900">New patient</p>
            <p className="text-[9px] text-slate-500 leading-snug">Register + book</p>
          </button>
          <button
            onClick={() => onLensScan('id')}
            className="rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 p-2.5 text-left transition-colors"
            style={{ cursor: 'pointer' }}
            data-testid="appt-agentic-scan-id"
          >
            <IdCard className="h-3.5 w-3.5 text-slate-700 mb-1" />
            <p className="text-[11px] font-semibold text-slate-900">Scan ID</p>
            <p className="text-[9px] text-slate-500 leading-snug">Camera · Lens</p>
          </button>
          <button
            onClick={() => onLensScan('insurance')}
            className="rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 p-2.5 text-left transition-colors"
            style={{ cursor: 'pointer' }}
            data-testid="appt-agentic-scan-insurance"
          >
            <CreditCard className="h-3.5 w-3.5 text-emerald-600 mb-1" />
            <p className="text-[11px] font-semibold text-slate-900">Scan insurance</p>
            <p className="text-[9px] text-slate-500 leading-snug">Auto-verify</p>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 py-1">
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-[10px] text-slate-400">or</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>
      <button
        onClick={onSwitchManual}
        className="w-full text-center text-[11px] text-slate-500 hover:text-slate-700"
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        data-testid="appt-agentic-switch-manual"
      >
        Switch to manual
      </button>
    </div>
  );
};

const LENS_DOC_PRESETS = {
  id: {
    title: 'Scan patient ID',
    hint: 'Point the camera at a driver\'s license, state ID, or passport.',
    icon: 'id',
    extracted: {
      patientName: 'Sarah Miller',
      referringMd: null,
      visitType: 'New Patient',
      urgency: 'Routine (within 30 days)',
      _capturedFrom: 'ID card',
    },
  },
  insurance: {
    title: 'Scan insurance card',
    hint: 'Capture both sides — Echo auto-verifies coverage in the background.',
    icon: 'insurance',
    extracted: {
      patientName: 'Sarah Miller',
      referringMd: null,
      visitType: 'New Patient',
      urgency: 'Routine (within 30 days)',
      _capturedFrom: 'Insurance card',
    },
  },
  referral: {
    title: 'Scan to book',
    hint: 'Drop a referral, fax, ID, or insurance card here.',
    icon: 'referral',
    extracted: {
      patientName: 'Sarah Miller',
      referringMd: 'Dr. James Park · NYU Langone',
      visitType: 'New Patient',
      urgency: 'Routine (within 30 days)',
    },
  },
};

/* ── Lens mode ── */
const LensMode = ({ onSwitchManual, onExtracted, docKind }) => {
  const [extracting, setExtracting] = useState(false);
  const preset = LENS_DOC_PRESETS[docKind] || LENS_DOC_PRESETS.referral;
  const KindIcon = preset.icon === 'id' ? IdCard : preset.icon === 'insurance' ? CreditCard : ScanLine;
  const runScan = () => {
    setExtracting(true);
    setTimeout(() => {
      setExtracting(false);
      onExtracted(preset.extracted);
    }, 1400);
  };
  return (
    <div className="p-6 space-y-4" data-testid="appt-lens-mode">
      <div className="flex items-center gap-2">
        <KindIcon className="h-4 w-4 text-slate-700" />
        <h3 className="text-[14px] font-semibold text-slate-900">{preset.title}</h3>
      </div>
      <div className="rounded-[10px] border-2 border-dashed border-slate-200 bg-slate-50 p-8 flex flex-col items-center justify-center gap-3" data-testid="appt-lens-dropzone">
        <KindIcon className="h-8 w-8 text-slate-300" />
        <p className="text-[11px] text-slate-500 text-center max-w-xs">{preset.hint}</p>
        <Button size="sm" className="text-[11px] h-8 bg-[#111] hover:bg-[#222]" disabled={extracting} onClick={runScan} data-testid="appt-lens-scan-btn">
          {extracting ? 'Extracting…' : 'Scan now'}
        </Button>
      </div>
      <div>
        <p className="text-[11px] text-slate-600 font-medium mb-1">What can I scan?</p>
        <ul className="text-[10px] text-slate-500 space-y-0.5">
          <li>· Referral letter or fax</li>
          <li>· Patient ID card (for new patient)</li>
          <li>· Insurance card (for new patient)</li>
          <li>· Printed schedule or day sheet</li>
        </ul>
      </div>
      <button
        onClick={onSwitchManual}
        className="w-full text-center text-[11px] text-slate-500 hover:text-slate-700"
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        data-testid="appt-lens-switch-manual"
      >
        Switch to manual
      </button>
    </div>
  );
};

/* ── Manual step 1: Patient ── */
const StepPatient = ({ patients, onPick, onNewManual, selected, autoOpenNew }) => {
  const [q, setQ] = useState('');
  const [newMode, setNewMode] = useState(autoOpenNew ? 'agentic' : null); // null | 'agentic' | 'lens' | 'manual'
  const [newForm, setNewForm] = useState({ firstName: '', lastName: '', dob: '', phone: '', insurance: '' });
  // AI dictation state for agentic new-patient flow
  const [aiText, setAiText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [listening, setListening] = useState(false);
  const [capturedDocs, setCapturedDocs] = useState({ id: null, insurance: null }); // { summary } for each
  const recognitionRef = useRef(null);
  const [speechSupported, setSpeechSupported] = useState(false);

  // Default the New patient entry to agentic when the parent flips autoOpenNew.
  useEffect(() => {
    if (autoOpenNew) setNewMode('agentic');
  }, [autoOpenNew]);

  // Initialise browser SpeechRecognition once (optional; agentic NL works without it).
  useEffect(() => {
    // eslint-disable-next-line no-undef
    const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
    if (!SR) return;
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = 'en-US';
    r.onresult = (e) => {
      const transcript = Array.from(e.results).map((x) => x[0].transcript).join(' ');
      setAiText(transcript);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recognitionRef.current = r;
    setSpeechSupported(true);
  }, []);

  const toggleListen = () => {
    const r = recognitionRef.current;
    if (!r) return;
    if (listening) { r.stop(); setListening(false); return; }
    try { r.start(); setListening(true); } catch { /* noop */ }
  };

  /* ── Lens mock-capture (ID or insurance) ── */
  const captureDoc = (kind) => {
    setCapturedDocs((prev) => ({
      ...prev,
      [kind]: kind === 'id'
        ? { summary: 'Driver\'s license · Sarah Miller · DOB 1990-05-14 · NYC' }
        : { summary: 'Aetna PPO · Member 7844-2219 · Active (verified)' },
    }));
    // Auto-prefill whatever fields we can derive
    if (kind === 'id') {
      setNewForm((f) => ({
        ...f,
        firstName: f.firstName || 'Sarah',
        lastName: f.lastName || 'Miller',
        dob: f.dob || '05/14/1990',
      }));
    }
    if (kind === 'insurance') {
      setNewForm((f) => ({ ...f, insurance: f.insurance || 'Aetna PPO' }));
    }
  };

  /* ── Parse natural-language → fields, then auto-create + pick patient ── */
  const runAgenticCreate = () => {
    setParsing(true);
    setTimeout(() => {
      const t = aiText.trim();
      // Name parser — accepts "for First Last" / "my name is First Last" / "First Last".
      let firstName = newForm.firstName;
      let lastName = newForm.lastName;
      const nameMatch = t.match(/(?:for\s+|name(?:\s+is)?\s+|i'm\s+|i am\s+)?([A-Z][a-zA-Z\-']+)\s+([A-Z][a-zA-Z\-']+)/);
      if (nameMatch) { firstName = nameMatch[1]; lastName = nameMatch[2]; }
      // DOB: MM/DD/YYYY or MM-DD-YYYY
      let dob = newForm.dob;
      const dobMatch = t.match(/\b(0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])[\/\-](19|20)\d{2}\b/);
      if (dobMatch) dob = dobMatch[0].replace(/-/g, '/');
      // Phone: (555) 123-4567, 555-123-4567, 5551234567
      let phone = newForm.phone;
      const phoneMatch = t.match(/(?:\(?\d{3}\)?[\s.\-]?)\d{3}[\s.\-]?\d{4}/);
      if (phoneMatch) phone = phoneMatch[0];
      // Insurance: keyword lookup
      let insurance = newForm.insurance;
      const insMatch = t.match(/\b(Aetna|BCBS|Blue Cross|United|Cigna|Humana|Medicare|Medicaid|Kaiser|Anthem)\b(?:\s+[A-Z]{2,3})?/i);
      if (insMatch) insurance = insMatch[0];

      // Apply Lens-captured fallbacks (priority: agentic text > Lens > existing)
      if (!firstName && capturedDocs.id) firstName = 'Sarah';
      if (!lastName && capturedDocs.id) lastName = 'Miller';
      if (!dob && capturedDocs.id) dob = '05/14/1990';
      if (!insurance && capturedDocs.insurance) insurance = 'Aetna PPO';

      if (!firstName || !lastName) {
        toast.error('Need at least a first and last name. Try: "Sarah Miller, DOB 05/14/1990, (555) 123-4567"');
        setParsing(false);
        return;
      }

      const id = `P-new-${Date.now()}`;
      const patient = {
        id,
        name: `${firstName} ${lastName}`,
        dob: dob || '—',
        phone: phone || '—',
        lastVisit: 'New Patient',
        preferredProvider: null,
        preferredLocation: null,
        preferredType: 'New Patient',
        referringMd: null,
        insurance: insurance || undefined,
        lensCaptured: Object.values(capturedDocs).filter(Boolean).map((d) => d.summary),
      };
      setParsing(false);
      toast.success(`Patient chart created for ${patient.name}`, {
        description: `Dob ${patient.dob} · ${patient.phone}${insurance ? ` · ${insurance}` : ''}${patient.lensCaptured.length ? ` · ${patient.lensCaptured.length} doc(s) on file` : ''}`,
      });
      onPick(patient);
      setNewMode(null);
      setAiText('');
      setCapturedDocs({ id: null, insurance: null });
      setNewForm({ firstName: '', lastName: '', dob: '', phone: '', insurance: '' });
    }, 900);
  };

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const ql = q.toLowerCase();
    return patients.filter((p) => p.name.toLowerCase().includes(ql) || p.dob.includes(q) || p.phone.includes(q)).slice(0, 6);
  }, [q, patients]);

  const submitNewPatient = () => {
    if (!newForm.firstName.trim() || !newForm.lastName.trim()) return;
    const id = `P-new-${Date.now()}`;
    const patient = {
      id,
      name: `${newForm.firstName.trim()} ${newForm.lastName.trim()}`,
      dob: newForm.dob || '—',
      phone: newForm.phone || '—',
      lastVisit: 'New Patient',
      preferredProvider: null,
      preferredLocation: null,
      preferredType: 'New Patient',
      referringMd: null,
    };
    onPick(patient);
    setNewMode(null);
    setNewForm({ firstName: '', lastName: '', dob: '', phone: '', insurance: '' });
  };

  return (
    <div className="space-y-3" data-testid="appt-step-patient">
      {/* Top CTA: prominent "New Patient" entry surfaced above search */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setNewMode(null); }}
            placeholder="Search patient by name, DOB or phone"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 text-[12px] outline-none focus:border-slate-400 bg-slate-50"
            data-testid="appt-patient-search"
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-9 text-[11px] gap-1 shrink-0 border-[#111] text-[#111] hover:bg-slate-100"
          onClick={() => setNewMode('agentic')}
          data-testid="appt-new-patient-btn"
        >
          <Sparkles className="h-3.5 w-3.5" /> New patient
        </Button>
      </div>

      {/* ═══ AGENTIC NEW PATIENT FLOW ═══
          Default entry for creating a new patient. One NL box + Lens for ID / insurance.
          On submit, AI parses → creates patient profile → selects them so the appointment
          step can immediately continue. "Switch to manual" escape hatch preserved. */}
      {newMode === 'agentic' && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="appt-new-patient-agentic">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold text-slate-800 inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-[#6b5ce7]" /> Create new patient · agentic
            </p>
            <button onClick={() => setNewMode(null)} className="text-[10px] text-slate-500 hover:text-slate-700" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>× Cancel</button>
          </div>
          <p className="text-[10px] text-slate-500">
            Type or dictate the patient&rsquo;s details — or scan their ID/insurance. Echo will auto-create the chart and attach everything to this appointment.
          </p>

          {/* NL dictation box with mic + Fill */}
          <div className="relative">
            <textarea
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              rows={2}
              placeholder='e.g. "Sarah Miller, DOB 05/14/1990, (512) 555-0120, Aetna PPO"'
              className="w-full bg-slate-50 rounded-lg border border-slate-200 px-3 py-2 pr-24 text-[12px] outline-none focus:border-slate-400 resize-none"
              data-testid="appt-new-patient-agentic-input"
            />
            <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1">
              {speechSupported && (
                <button
                  onClick={toggleListen}
                  aria-label={listening ? 'Stop voice' : 'Start voice'}
                  className="h-7 w-7 rounded-full inline-flex items-center justify-center border border-slate-200 bg-white"
                  style={{ background: listening ? '#111' : '#fff', color: listening ? '#fff' : '#475569', cursor: 'pointer' }}
                  data-testid="appt-new-patient-mic"
                >
                  {listening ? <Mic className="h-3 w-3 animate-pulse" /> : <MicOff className="h-3 w-3" />}
                </button>
              )}
              <button
                onClick={runAgenticCreate}
                disabled={parsing || (!aiText.trim() && !capturedDocs.id && !capturedDocs.insurance)}
                className="h-7 px-3 rounded-full inline-flex items-center gap-1 text-[10px] font-semibold bg-[#111] text-white disabled:opacity-40"
                style={{ border: 'none', cursor: 'pointer' }}
                data-testid="appt-new-patient-create"
              >
                {parsing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                {parsing ? 'Creating…' : 'Create + book'}
              </button>
            </div>
          </div>

          {/* Lens capture rail */}
          <div>
            <p className="text-[9px] uppercase font-semibold text-slate-400 tracking-wide mb-1.5">
              Scan documents (optional · speeds up check-in)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => captureDoc('id')}
                className={cn('text-left p-2.5 rounded-lg border transition-all',
                  capturedDocs.id ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                )}
                style={{ cursor: 'pointer' }}
                data-testid="appt-new-patient-scan-id"
              >
                <div className="flex items-center justify-between">
                  <IdCard className="h-3.5 w-3.5 text-slate-700" />
                  {capturedDocs.id && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                </div>
                <p className="text-[11px] font-semibold text-slate-900 mt-1">Scan ID card</p>
                <p className="text-[9px] text-slate-500 line-clamp-2 mt-0.5">
                  {capturedDocs.id?.summary || "Driver's license, state ID, or passport"}
                </p>
              </button>
              <button
                onClick={() => captureDoc('insurance')}
                className={cn('text-left p-2.5 rounded-lg border transition-all',
                  capturedDocs.insurance ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                )}
                style={{ cursor: 'pointer' }}
                data-testid="appt-new-patient-scan-insurance"
              >
                <div className="flex items-center justify-between">
                  <CreditCard className="h-3.5 w-3.5 text-emerald-600" />
                  {capturedDocs.insurance && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                </div>
                <p className="text-[11px] font-semibold text-slate-900 mt-1">Scan insurance</p>
                <p className="text-[9px] text-slate-500 line-clamp-2 mt-0.5">
                  {capturedDocs.insurance?.summary || 'Auto-verifies eligibility'}
                </p>
              </button>
            </div>
          </div>

          <div className="pt-1">
            <button
              onClick={() => setNewMode('manual')}
              className="text-[10px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              data-testid="appt-new-patient-switch-manual"
            >
              Prefer to type every field? Switch to manual →
            </button>
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden" data-testid="appt-patient-results">
          {results.map((p) => (
            <button
              key={p.id}
              onClick={() => { onPick(p); setNewMode(null); }}
              className={cn('w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors', selected?.id === p.id && 'bg-slate-100')}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              data-testid={`appt-patient-${p.id}`}
            >
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-slate-100 inline-flex items-center justify-center">
                  <User className="h-3.5 w-3.5 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[12px] font-medium text-slate-900 truncate">{p.name}</p>
                    <span className="text-[10px] text-slate-400">· DOB {p.dob}</span>
                  </div>
                  <p className="text-[10px] text-slate-400">Last visit: {p.lastVisit}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {q && results.length === 0 && !newMode && (
        <div className="p-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 text-center" data-testid="appt-patient-no-match">
          <p className="text-[11px] text-slate-600 mb-2">No patient found matching &quot;{q}&quot;</p>
          <div className="flex gap-2 justify-center">
            <Button size="sm" variant="outline" className="text-[10px] gap-1 h-7" onClick={() => setNewMode('agentic')} data-testid="appt-new-patient-lens">
              <ScanLine className="h-3 w-3" /> Scan with Lens
            </Button>
            <Button size="sm" className="text-[10px] gap-1 h-7 bg-[#111] hover:bg-[#000]" onClick={() => { setAiText(q); setNewMode('agentic'); }} data-testid="appt-new-patient-from-search">
              <Sparkles className="h-3 w-3" /> Create new patient
            </Button>
          </div>
        </div>
      )}

      {/* New patient forms */}
      {newMode === 'manual' && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid="appt-new-patient-form">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold text-slate-800 inline-flex items-center gap-1">
              <Plus className="h-3 w-3 text-[#111]" /> Add new patient
            </p>
            <button onClick={() => setNewMode(null)} className="text-[10px] text-slate-500 hover:text-slate-700" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>× Cancel</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] text-slate-500">First name</label>
              <input value={newForm.firstName} onChange={(e) => setNewForm({ ...newForm, firstName: e.target.value })} className="w-full mt-0.5 px-2 py-1.5 rounded-md border border-slate-200 text-[11px] outline-none bg-slate-50" data-testid="appt-new-firstname" />
            </div>
            <div>
              <label className="text-[9px] text-slate-500">Last name</label>
              <input value={newForm.lastName} onChange={(e) => setNewForm({ ...newForm, lastName: e.target.value })} className="w-full mt-0.5 px-2 py-1.5 rounded-md border border-slate-200 text-[11px] outline-none bg-slate-50" data-testid="appt-new-lastname" />
            </div>
            <div>
              <label className="text-[9px] text-slate-500">DOB (MM/DD/YYYY)</label>
              <input value={newForm.dob} onChange={(e) => setNewForm({ ...newForm, dob: e.target.value })} placeholder="01/01/1990" className="w-full mt-0.5 px-2 py-1.5 rounded-md border border-slate-200 text-[11px] outline-none bg-slate-50" data-testid="appt-new-dob" />
            </div>
            <div>
              <label className="text-[9px] text-slate-500">Phone</label>
              <input value={newForm.phone} onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })} placeholder="(555) 123-4567" className="w-full mt-0.5 px-2 py-1.5 rounded-md border border-slate-200 text-[11px] outline-none bg-slate-50" data-testid="appt-new-phone" />
            </div>
            <div className="col-span-2">
              <label className="text-[9px] text-slate-500">Insurance (optional)</label>
              <input value={newForm.insurance} onChange={(e) => setNewForm({ ...newForm, insurance: e.target.value })} className="w-full mt-0.5 px-2 py-1.5 rounded-md border border-slate-200 text-[11px] outline-none bg-slate-50" data-testid="appt-new-insurance" />
            </div>
          </div>
          <Button
            size="sm"
            className="w-full h-8 text-[11px] bg-[#111] hover:bg-[#000] gap-1"
            disabled={!newForm.firstName.trim() || !newForm.lastName.trim()}
            onClick={submitNewPatient}
            data-testid="appt-new-patient-submit"
          >
            <CheckCircle2 className="h-3 w-3" /> Add patient &amp; continue
          </Button>
          {onNewManual && (
            <button
              onClick={() => { onNewManual('lens'); setNewMode(null); }}
              className="w-full text-center text-[10px] text-slate-500 hover:text-slate-700 pt-1"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              data-testid="appt-new-switch-lens"
            >
              or scan ID/insurance with Lens →
            </button>
          )}
        </div>
      )}
      {newMode === 'lens' && (
        <div className="rounded-lg border-2 border-dashed border-[#111] bg-slate-100 p-4 text-center" data-testid="appt-new-patient-lens-scan">
          <ScanLine className="h-6 w-6 text-[#111] mx-auto mb-1.5" />
          <p className="text-[11px] font-semibold text-slate-800">Scan ID or insurance card</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Lens will extract name, DOB, phone, insurance</p>
          <div className="flex gap-2 justify-center mt-2">
            <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={() => setNewMode(null)}>Cancel</Button>
            <Button
              size="sm"
              className="text-[10px] h-7 bg-[#111] hover:bg-[#000]"
              onClick={() => {
                // Mock: fake-extract a "scanned" patient
                const scanned = {
                  id: `P-scan-${Date.now()}`,
                  name: 'Sarah Miller',
                  dob: '04/18/1991',
                  phone: '(555) 777-1212',
                  lastVisit: 'New Patient',
                  preferredProvider: null,
                  preferredLocation: null,
                  preferredType: 'New Patient',
                  referringMd: null,
                };
                onPick(scanned);
                setNewMode(null);
              }}
              data-testid="appt-new-patient-scan-now"
            >
              Scan now
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Manual step 2: Appointment ── */
const StepAppointment = ({
  locations, providers, visitTypes, openSlots,
  patient, selection, setSelection,
}) => {
  const primaryTypes = visitTypes.filter((t) => t.showAsDefault && t.status === 'enabled');
  const secondaryTypes = visitTypes.filter((t) => !t.showAsDefault && t.status === 'enabled');
  const [showMore, setShowMore] = useState(false);

  // Filter slots by selected location + provider
  const filteredSlots = openSlots.filter(
    (s) => s.location === selection.locationId && (!selection.provider || s.provider === selection.provider)
  );

  return (
    <div className="space-y-4" data-testid="appt-step-appointment">
      {/* Location */}
      {locations.length > 1 && (
        <div>
          <p className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide mb-1.5">Location</p>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="appt-locations">
            {locations.map((loc) => (
              <div key={loc.id} className="flex flex-col items-start">
                <Pill active={selection.locationId === loc.id} onClick={() => setSelection((s) => ({ ...s, locationId: loc.id }))} testId={`appt-loc-${loc.id}`}>
                  {loc.name}
                </Pill>
                {patient?.preferredLocation === loc.id && selection.locationId === loc.id && (
                  <span className="text-[9px] text-[#111] mt-0.5 inline-flex items-center gap-0.5"><Sparkles className="h-2 w-2" /> suggested</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Provider */}
      <div>
        <p className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide mb-1.5">Provider</p>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="appt-providers">
          {providers.map((prov) => {
            const locInfo = locations.find((l) => l.id === selection.locationId);
            const atLocation = !locInfo || locInfo.providers.includes(prov);
            return (
              <div key={prov} className="flex flex-col items-start">
                <Pill
                  active={selection.provider === prov}
                  onClick={() => atLocation && setSelection((s) => ({ ...s, provider: prov }))}
                  disabled={!atLocation}
                  testId={`appt-prov-${prov.replace(/\s+/g, '-')}`}
                >
                  {prov}
                </Pill>
                {patient?.preferredProvider === prov && selection.provider === prov && (
                  <span className="text-[9px] text-[#111] mt-0.5 inline-flex items-center gap-0.5"><Sparkles className="h-2 w-2" /> suggested</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Visit Type tiles */}
      <div>
        <p className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide mb-1.5">Visit type</p>
        <div className="flex gap-1.5 flex-wrap" data-testid="appt-visit-types-primary">
          {primaryTypes.map((vt) => (
            <button
              key={vt.id}
              onClick={() => setSelection((s) => ({ ...s, visitTypeId: vt.id, durationMin: vt.defaultDuration }))}
              className={cn(
                'rounded-md px-3 py-1.5 text-[11px] font-medium transition-colors border',
                selection.visitTypeId === vt.id
                  ? 'bg-[#111] text-white border-[#111]'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
              )}
              style={{ cursor: 'pointer' }}
              data-testid={`appt-vt-${vt.id}`}
            >
              {vt.name} {vt.defaultDuration}m
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-[10px] text-slate-500 mt-2 hover:text-slate-700"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          data-testid="appt-vt-more-toggle"
        >
          {showMore ? '▴ Less' : '▾ More visit types'}
        </button>
        {showMore && (
          <div className="flex gap-1.5 flex-wrap mt-2" data-testid="appt-visit-types-secondary">
            {secondaryTypes.map((vt) => (
              <button
                key={vt.id}
                onClick={() => setSelection((s) => ({ ...s, visitTypeId: vt.id, durationMin: vt.defaultDuration }))}
                className={cn(
                  'rounded-md px-2.5 py-1 text-[10px] font-medium transition-colors border',
                  selection.visitTypeId === vt.id
                    ? 'bg-[#111] text-white border-[#111]'
                    : 'bg-slate-50 text-slate-700 border-slate-100 hover:border-slate-300'
                )}
                style={{ cursor: 'pointer' }}
                data-testid={`appt-vt-${vt.id}`}
              >
                {vt.name}
              </button>
            ))}
          </div>
        )}
        {/* Referring physician contextual hint */}
        {(() => {
          const vt = visitTypes.find((x) => x.id === selection.visitTypeId);
          if (!vt?.flags?.referringRequired) return null;
          return (
            <div className="mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-100 text-[10px] flex items-start gap-1.5" data-testid="appt-referring-hint">
              <AlertTriangle className="h-3 w-3 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-800 font-medium">This visit type requires a referring physician.</p>
                {patient?.referringMd
                  ? <p className="text-amber-700">Referring MD on file: <b>{patient.referringMd}</b> ✓</p>
                  : <p className="text-amber-700">No referring physician on file. <button className="underline" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>Add now</button></p>}
              </div>
            </div>
          );
        })()}
      </div>
      {/* Duration */}
      <div>
        <p className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide mb-1.5">Duration</p>
        <div className="flex gap-1.5" data-testid="appt-durations">
          {[15, 20, 30, 45, 60].map((d) => (
            <button
              key={d}
              onClick={() => setSelection((s) => ({ ...s, durationMin: d }))}
              className={cn(
                'px-3 py-1 rounded-md text-[11px] font-medium border',
                selection.durationMin === d ? 'bg-[#111] text-white border-[#111]' : 'bg-white text-slate-600 border-slate-200'
              )}
              style={{ cursor: 'pointer' }}
              data-testid={`appt-dur-${d}`}
            >
              {d}m
            </button>
          ))}
        </div>
      </div>
      {/* Slots */}
      <div>
        <p className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide mb-1.5">Available slots</p>
        <div className="space-y-1" data-testid="appt-slots">
          {filteredSlots.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
              <p className="text-[11px] text-slate-600">No slots available for this selection.</p>
              <div className="flex gap-1.5 justify-center mt-2">
                <Button size="sm" variant="outline" className="text-[10px] h-7">Check tomorrow</Button>
                <Button size="sm" variant="outline" className="text-[10px] h-7">All providers</Button>
                <Button size="sm" variant="outline" className="text-[10px] h-7">Add to waitlist</Button>
              </div>
            </div>
          )}
          {filteredSlots.map((s, i) => {
            const slotKey = `${s.time}-${s.provider}-${s.location}`;
            const active = selection.slotKey === slotKey;
            return (
              <button
                key={slotKey}
                onClick={() => setSelection((sel) => ({ ...sel, slotKey, slot: s }))}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors',
                  active ? 'border-[#111] bg-slate-100' : 'border-slate-200 bg-white hover:border-slate-300'
                )}
                style={{ cursor: 'pointer' }}
                data-testid={`appt-slot-${i}`}
              >
                <div className="flex items-center gap-3 text-[11px]">
                  <span className={cn('h-2.5 w-2.5 rounded-full border', active ? 'bg-[#111] border-[#111]' : 'border-slate-300')} />
                  <span className="font-semibold text-slate-900">{s.time}</span>
                  <span className="text-slate-500">{s.provider}</span>
                  <span className="text-slate-400">{locations.find((l) => l.id === s.location)?.name}</span>
                </div>
                {s.recommended && (
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-300">
                    <Sparkles className="h-2 w-2 inline mr-0.5" /> Recommended
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ── Manual/All: step 3 Confirm ── */
const StepConfirm = ({ patient, selection, locations, visitTypes, prefillFields = [] }) => {
  const vt = visitTypes.find((x) => x.id === selection.visitTypeId);
  const loc = locations.find((l) => l.id === selection.locationId);
  return (
    <div className="space-y-3" data-testid="appt-step-confirm">
      {prefillFields.length > 0 && (
        <div className="rounded-lg bg-slate-100 border border-slate-300 p-3 text-[10px] text-slate-700 flex items-center gap-2" data-testid="appt-prefill-strip">
          <Sparkles className="h-3 w-3" />
          <span>Echo pre-filled {prefillFields.length} fields from {patient?.name || 'history'} — {prefillFields.join(' · ')}</span>
        </div>
      )}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="appt-confirm-card">
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          <div className="h-10 w-10 rounded-full bg-slate-100 inline-flex items-center justify-center">
            <User className="h-4 w-4 text-slate-500" />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-slate-900">{patient?.name || '—'}</p>
            <p className="text-[10px] text-slate-400">DOB: {patient?.dob || '—'}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[9px] text-slate-400 uppercase font-semibold">Date & time</p>
            <p className="text-[11px] font-medium text-slate-800 inline-flex items-center gap-1 mt-0.5">
              <Clock className="h-3 w-3 text-slate-400" /> {selection.slot?.time || '—'} · Today
            </p>
          </div>
          <div>
            <p className="text-[9px] text-slate-400 uppercase font-semibold">Provider</p>
            <p className="text-[11px] font-medium text-slate-800 mt-0.5">{selection.provider || '—'}</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-400 uppercase font-semibold">Type</p>
            <p className="text-[11px] font-medium text-slate-800 mt-0.5">{vt?.name || '—'} ({selection.durationMin || vt?.defaultDuration}m)</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-400 uppercase font-semibold">Location</p>
            <p className="text-[11px] font-medium text-slate-800 inline-flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3 text-slate-400" /> {loc?.name || '—'}
            </p>
          </div>
        </div>
        {vt?.flags?.nonBillable && (
          <div className="rounded-md bg-amber-50 border border-amber-100 p-2 text-[10px] text-amber-800 flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3" /> Non-billable visit type — flagged in RevQ.
          </div>
        )}
        <div className="pt-2 border-t border-slate-100 space-y-0.5">
          <p className="text-[10px] text-slate-500 inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Intake forms will be sent to patient
          </p>
          <p className="text-[10px] text-slate-500 inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Reminder sequence: 48h, 24h, 2h before
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Main lightbox ── */
const NewAppointmentLightbox = ({
  open,
  onOpenChange,
  prefillTime,
  patients = [],
  locations = [],
  providers = [],
  visitTypes = [],
  openSlots = [],
  onConfirm = () => {},
}) => {
  const { isDemo } = useAppMode();
  const [mode, setMode] = useState(null); // null = picker
  const [lastMode, setLastMode] = useState('manual');
  const [step, setStep] = useState(1); // manual: 1..3, other modes: 3
  const [patient, setPatient] = useState(null);
  const [lensDocKind, setLensDocKind] = useState(null); // 'id' | 'insurance' | 'referral'
  const [newPatientAutoOpen, setNewPatientAutoOpen] = useState(false);
  const [selection, setSelection] = useState({
    locationId: locations[0]?.id,
    provider: providers[0],
    visitTypeId: visitTypes.find((v) => v.showAsDefault)?.id || visitTypes[0]?.id,
    durationMin: 30,
    slotKey: null,
    slot: null,
  });
  const [prefillFields, setPrefillFields] = useState([]);

  // Reset when opened
  useEffect(() => {
    if (open) {
      setMode(null);
      setStep(1);
      setPatient(null);
      setPrefillFields([]);
      setLensDocKind(null);
      setNewPatientAutoOpen(false);
      setSelection((s) => ({ ...s, slotKey: null, slot: prefillTime ? { time: prefillTime, provider: providers[0], location: locations[0]?.id } : null }));
    }
  }, [open, prefillTime, providers, locations]);

  const handlePickMode = (m) => {
    setMode(m);
    setLastMode(m);
    if (m === 'manual') setStep(1);
  };

  const handlePickPatient = (p) => {
    setPatient(p);
    // AI pre-fill Step 2
    const fields = [];
    if (p.preferredProvider) {
      setSelection((s) => ({ ...s, provider: p.preferredProvider }));
      fields.push('Provider');
    }
    if (p.preferredLocation) {
      setSelection((s) => ({ ...s, locationId: p.preferredLocation }));
      fields.push('Location');
    }
    const type = visitTypes.find((v) => v.name === p.preferredType);
    if (type) {
      setSelection((s) => ({ ...s, visitTypeId: type.id, durationMin: type.defaultDuration }));
      fields.push('Visit type');
    }
    setPrefillFields(fields);
    setStep(2);
  };

  const handleAgenticSubmit = () => {
    // Demo helper: auto-resolves to the first patient + recommended slot.
    // Real mode pre-fills nothing — the user must type details manually.
    const pick = patients[0];
    setPatient(pick);
    const rec = openSlots.find((s) => s.recommended) || openSlots[0];
    const vt = visitTypes.find((v) => v.name === pick?.preferredType) || visitTypes[0];
    setSelection({
      locationId: rec?.location || locations[0]?.id,
      provider: rec?.provider || providers[0],
      visitTypeId: vt?.id,
      durationMin: vt?.defaultDuration || 30,
      slotKey: rec ? `${rec.time}-${rec.provider}-${rec.location}` : null,
      slot: rec,
    });
    setPrefillFields(['Provider', 'Location', 'Visit type', 'Time']);
    setStep(3);
    setMode('agentic');
  };

  const handleLensExtracted = (data) => {
    const match = patients.find((p) => p.name === data.patientName) || patients[0];
    setPatient(match);
    const vt = visitTypes.find((v) => v.name === data.visitType) || visitTypes[0];
    const rec = openSlots.find((s) => s.recommended) || openSlots[0];
    setSelection({
      locationId: rec?.location || locations[0]?.id,
      provider: rec?.provider || providers[0],
      visitTypeId: vt?.id,
      durationMin: vt?.defaultDuration || 30,
      slotKey: rec ? `${rec.time}-${rec.provider}-${rec.location}` : null,
      slot: rec,
    });
    setPrefillFields(['Patient', 'Visit type', 'Referring MD', 'Time']);
    setStep(3);
  };

  const canConfirm = !!(patient && selection.visitTypeId && selection.slot);

  const handleConfirm = () => {
    onConfirm({ patient, selection });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] p-0 gap-0 overflow-hidden" data-testid="new-appointment-lightbox">
        {mode === null && (
          <>
            <DialogHeader className="px-6 pt-5 pb-1">
              <DialogTitle className="text-[14px] font-semibold">New appointment</DialogTitle>
              <DialogDescription className="sr-only">Choose how to create a new appointment</DialogDescription>
            </DialogHeader>
            <ModeSelector last={lastMode} onPick={handlePickMode} />
          </>
        )}
        {mode === 'agentic' && step < 3 && (
          <>
            <DialogHeader className="px-6 pt-5 pb-0">
              <DialogTitle className="sr-only">Ask Echo</DialogTitle>
              <DialogDescription className="sr-only">Describe the appointment and Echo will book it</DialogDescription>
            </DialogHeader>
            <AgenticMode
              onSwitchManual={() => { setMode('manual'); setStep(1); }}
              onSubmit={handleAgenticSubmit}
              onNewPatient={() => {
                setMode('manual');
                setStep(1);
                setNewPatientAutoOpen(true);
              }}
              onLensScan={(kind) => {
                setLensDocKind(kind);
                setMode('lens');
                setStep(1);
              }}
            />
          </>
        )}
        {mode === 'lens' && step < 3 && (
          <>
            <DialogHeader className="px-6 pt-5 pb-0">
              <DialogTitle className="sr-only">Scan to book</DialogTitle>
              <DialogDescription className="sr-only">Scan a referral or ID to auto-fill the appointment</DialogDescription>
            </DialogHeader>
            <LensMode onSwitchManual={() => { setMode('manual'); setStep(1); }} onExtracted={handleLensExtracted} docKind={lensDocKind} />
          </>
        )}
        {mode === 'manual' && (
          <>
            <DialogHeader className="px-6 pt-5 pb-3">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-[14px] font-semibold">
                  {step === 1 ? 'Select patient' : step === 2 ? 'Appointment details' : 'Confirm'}
                </DialogTitle>
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3].map((n) => (
                    <span
                      key={n}
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        n === step ? 'bg-[#111]' : n < step ? 'bg-slate-400' : 'bg-slate-200'
                      )}
                    />
                  ))}
                </div>
              </div>
              <DialogDescription className="sr-only">Step {step} of 3</DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 160px)' }}>
              {step === 1 && (
                <StepPatient
                  patients={patients}
                  selected={patient}
                  onPick={handlePickPatient}
                  onNewManual={() => { /* Stub: inline form */ }}
                  autoOpenNew={newPatientAutoOpen}
                />
              )}
              {step === 2 && (
                <StepAppointment
                  locations={locations}
                  providers={providers}
                  visitTypes={visitTypes}
                  openSlots={openSlots}
                  patient={patient}
                  selection={selection}
                  setSelection={setSelection}
                />
              )}
              {step === 3 && (
                <StepConfirm
                  patient={patient}
                  selection={selection}
                  locations={locations}
                  visitTypes={visitTypes}
                  prefillFields={prefillFields}
                />
              )}
            </div>
          </>
        )}
        {/* Agentic/Lens confirm step shares the same confirm UI */}
        {(mode === 'agentic' || mode === 'lens') && step === 3 && (
          <>
            <DialogHeader className="px-6 pt-5 pb-3">
              <DialogTitle className="text-[14px] font-semibold inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-[#111]" />
                Echo found everything — confirm below
              </DialogTitle>
              <DialogDescription className="sr-only">Review and confirm</DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 160px)' }}>
              <StepConfirm
                patient={patient}
                selection={selection}
                locations={locations}
                visitTypes={visitTypes}
                prefillFields={prefillFields}
              />
            </div>
          </>
        )}
        {/* Footer */}
        {mode && (
          <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between gap-2 bg-white">
            <Button
              variant="ghost"
              size="sm"
              className="text-[11px] gap-1 text-slate-500"
              onClick={() => {
                if (mode === 'manual' && step > 1) setStep(step - 1);
                else setMode(null);
              }}
              data-testid="appt-back-btn"
            >
              <ArrowLeft className="h-3 w-3" /> Back
            </Button>
            {mode === 'manual' && step === 1 && (
              <Button
                size="sm"
                className="text-[11px] bg-[#111] hover:bg-[#222]"
                disabled={!patient}
                onClick={() => setStep(2)}
                data-testid="appt-continue-btn"
              >
                Continue
              </Button>
            )}
            {mode === 'manual' && step === 2 && (
              <Button
                size="sm"
                className="text-[11px] bg-[#111] hover:bg-[#222]"
                disabled={!selection.slot}
                onClick={() => setStep(3)}
                data-testid="appt-continue-btn"
              >
                Review & Confirm
              </Button>
            )}
            {step === 3 && (
              <Button
                size="sm"
                className="text-[11px] bg-[#111] hover:bg-[#222] gap-1"
                disabled={!canConfirm}
                onClick={handleConfirm}
                data-testid="appt-confirm-btn"
              >
                <CheckCircle2 className="h-3 w-3" /> Confirm Appointment
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewAppointmentLightbox;
